import { useNavigate } from '@tanstack/react-router';
import { Bot, CheckCircle, Send } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MessageBubble } from './message-bubble';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '~/components/ui/dialog';
import type { InterviewMessageResponse } from '~/lib/interview-api';
import { restartInterview, sendMessage } from '~/lib/interview-api';

interface ChatMessage {
  id: string;
  role: 'assistant' | 'candidate';
  content: string;
}

interface ChatInterfaceProps {
  token: string;
  jobTitle: string;
  employer: string | null;
  initialMessages: InterviewMessageResponse[];
  onCompleted: () => void;
}

export function ChatInterface({
  token,
  jobTitle,
  employer,
  initialMessages,
  onCompleted
}: ChatInterfaceProps): React.ReactNode {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    initialMessages.map((m) => ({ id: m.id, role: m.role, content: m.content }))
  );
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [interviewFinished, setInterviewFinished] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRestartModal, setShowRestartModal] = useState(false);
  const [restartNotes, setRestartNotes] = useState('');
  const [isRestarting, setIsRestarting] = useState(false);
  const [restartError, setRestartError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pasteDetectedRef = useRef(false);
  const isTouchDevice = useRef(
    typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
  );

  // Instant scroll during streaming to avoid jank on low-end devices
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
  }, [streamingContent]);

  // Smooth scroll for completed messages and finish state
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, interviewFinished]);

  // Focus on mount — desktop only (mobile: keyboard would pop up before user reads anything)
  useEffect(() => {
    if (!isTouchDevice.current) {
      inputRef.current?.focus();
    }
  }, []);

  // Re-focus after streaming — desktop only
  useEffect(() => {
    if (!isStreaming && !isTouchDevice.current) {
      inputRef.current?.focus();
    }
  }, [isStreaming]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = e.clipboardData.getData('text');
    if (pasted.length > 15) {
      pasteDetectedRef.current = true;
    }
  }, []);

  // Auto-resize textarea
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${String(Math.min(e.target.scrollHeight, 120) + 2)}px`;
  }, []);

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming || interviewFinished) return;

    setError(null);
    setInput('');
    setIsStreaming(true);
    setStreamingContent('');

    const copyPasteDetected = pasteDetectedRef.current;
    pasteDetectedRef.current = false;

    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }

    // Add candidate message immediately (optimistic)
    const candidateMsg: ChatMessage = {
      id: `candidate-${String(Date.now())}`,
      role: 'candidate',
      content: trimmed
    };
    setMessages((prev) => [...prev, candidateMsg]);

    try {
      let accumulated = '';

      await sendMessage(token, trimmed, copyPasteDetected, {
        onChunk: (text) => {
          accumulated += text;
          setStreamingContent(accumulated);
        },
        onDone: (completed) => {
          setMessages((prev) => [
            ...prev,
            {
              id: `assistant-${String(Date.now())}`,
              role: 'assistant',
              content: accumulated
            }
          ]);
          setStreamingContent('');
          setIsStreaming(false);

          if (completed) {
            setInterviewFinished(true);
          }
        },
        onError: (errMsg) => {
          setError(errMsg);
          setIsStreaming(false);
          setStreamingContent('');
        }
      });
    } catch {
      setError(t('screening.error.sendFailed'));
      setIsStreaming(false);
      setStreamingContent('');
    }
  }, [input, isStreaming, interviewFinished, token, t]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleSend();
      }
    },
    [handleSend]
  );

  const handleRestart = useCallback(async () => {
    if (!restartNotes.trim()) return;
    setIsRestarting(true);
    setRestartError(null);
    try {
      const { token: newToken } = await restartInterview(token, restartNotes);
      await navigate({ to: '/interview/$token', params: { token: newToken }, replace: true });
    } catch {
      setRestartError(t('screening.technicalIssue.submitError'));
      setIsRestarting(false);
    }
  }, [token, restartNotes, navigate, t]);

  const handleCloseRestartModal = useCallback(() => {
    setShowRestartModal(false);
    setRestartNotes('');
    setRestartError(null);
  }, []);

  return (
    <div className="flex h-dvh flex-col bg-background">
      {/* Header */}
      <div className="flex-none border-b border-border bg-card px-4 py-3">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-full bg-primary/10">
            <Bot className="size-5 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium">{t('screening.aiDisclosure')}</p>
            <p className="truncate text-xs text-muted-foreground">
              {jobTitle}
              {employer ? ` — ${employer}` : ''}
            </p>
          </div>
          <button
            className="ml-auto shrink-0 text-xs text-muted-foreground underline-offset-2 hover:underline"
            onClick={() => {
              setShowRestartModal(true);
            }}
          >
            {t('screening.technicalIssue.buttonLabel')}
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4" role="log" aria-live="polite">
        <div className="mx-auto flex max-w-2xl flex-col gap-3">
          {messages.map((msg) => (
            <MessageBubble key={msg.id} role={msg.role} content={msg.content} />
          ))}

          {/* Streaming message */}
          {isStreaming && streamingContent && (
            <MessageBubble role="assistant" content={streamingContent} />
          )}

          {/* Streaming indicator when no content yet */}
          {isStreaming && !streamingContent && (
            <div className="flex justify-start" role="status" aria-label={t('screening.typing')}>
              <div className="rounded-2xl rounded-bl-md bg-card px-4 py-3 shadow-sm border border-border">
                <div className="flex gap-1">
                  <span className="size-2 animate-bounce rounded-full bg-muted-foreground/40 [animation-delay:0ms]" />
                  <span className="size-2 animate-bounce rounded-full bg-muted-foreground/40 [animation-delay:150ms]" />
                  <span className="size-2 animate-bounce rounded-full bg-muted-foreground/40 [animation-delay:300ms]" />
                </div>
              </div>
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="mx-auto rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Bottom bar */}
      <div className="flex-none border-t border-border bg-card px-4 py-3">
        <div className="mx-auto max-w-2xl">
          {interviewFinished ? (
            <Button className="w-full h-12" onClick={onCompleted}>
              <CheckCircle className="size-4" />
              {t('screening.submitInterview')}
            </Button>
          ) : (
            <div className="flex items-end gap-2">
              <label htmlFor="chat-input" className="sr-only">
                {t('screening.typeMessage')}
              </label>
              <textarea
                id="chat-input"
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={t('screening.typeMessage')}
                disabled={isStreaming}
                rows={1}
                className="min-h-[44px] max-h-[120px] flex-1 resize-none rounded-xl border border-input bg-background px-4 py-3 text-base outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50"
              />
              <Button
                size="icon"
                className="size-11 shrink-0 rounded-xl"
                disabled={!input.trim() || isStreaming}
                onClick={() => void handleSend()}
                aria-label={t('screening.send')}
              >
                <Send className="size-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Technical issue modal */}
      <Dialog open={showRestartModal} onOpenChange={handleCloseRestartModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('screening.technicalIssue.modalTitle')}</DialogTitle>
            <DialogDescription>{t('screening.technicalIssue.modalDescription')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium" htmlFor="restart-notes">
              {t('screening.technicalIssue.notesLabel')}
            </label>
            <textarea
              id="restart-notes"
              value={restartNotes}
              onChange={(e) => {
                setRestartNotes(e.target.value);
              }}
              placeholder={t('screening.technicalIssue.notesPlaceholder')}
              disabled={isRestarting}
              maxLength={2000}
              rows={4}
              className="resize-none rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50"
            />
            {restartError && <p className="text-sm text-destructive">{restartError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={isRestarting} onClick={handleCloseRestartModal}>
              {t('screening.technicalIssue.cancel')}
            </Button>
            <Button
              disabled={isRestarting || !restartNotes.trim()}
              onClick={() => void handleRestart()}
            >
              {t('screening.technicalIssue.submitButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
